/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import React from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useAuthStore } from '../../stores/authStore';

const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

interface RequireAuthProps {
  children: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const setUser = useAuthStore((s) => s.setUser);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const isFetchingMe = useAuthStore((s) => s.isFetchingMe);

  React.useEffect(() => {
    if (DEV_BYPASS) {
      setUser({
        oid: 'dev-user-00000000-0000-0000-0000-000000000000',
        name: 'Dev User',
        email: 'dev@datamave.local',
        role: (import.meta.env.VITE_DEV_AUTH_ROLE as 'Admin' | 'Manager' | 'Pharmacist') || 'Admin',
      });
      return;
    }

    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      const claims = account.idTokenClaims as Record<string, unknown> | undefined;
      // Set basic identity from JWT token first
      setUser({
        oid: (claims?.oid as string) || account.localAccountId,
        name: account.name || 'Unknown',
        email: ((claims?.upn as string) || (claims?.preferred_username as string) || (claims?.email as string) || '').toLowerCase(),
        role: null, // Will be resolved by fetchMe
      });
      // Then fetch DB-managed role
      fetchMe();
    }
  }, [isAuthenticated, accounts, setUser, fetchMe]);

  if (DEV_BYPASS) {
    return <>{children}</>;
  }

  if (inProgress !== 'none' || isFetchingMe) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-dds-blue-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">
            {inProgress !== 'none' ? 'Authenticating...' : 'Loading profile...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-4">
        {/* Logo / Brand */}
        <div className="mb-8 text-center">
          <div className="text-4xl font-bold text-white tracking-tight mb-1">
            Data<span className="text-dds-blue-400">Mave</span>
          </div>
          <div className="text-sm text-gray-500 uppercase tracking-widest">
            Formulary
          </div>
        </div>

        {/* Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-6">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-white mb-2">Welcome back</h1>
            <p className="text-sm text-gray-400">
              Sign in with your Dobler Data Solutions account to continue.
            </p>
          </div>

          <button
            onClick={() => instance.loginRedirect(loginRequest)}
            className="w-full flex items-center justify-center gap-3 bg-dds-blue-500 hover:bg-dds-blue-600 active:bg-dds-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-dds-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            {/* Microsoft icon */}
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            Sign in with Microsoft
          </button>

          <p className="text-xs text-gray-600 text-center">
            Access is restricted to authorized personnel only.
          </p>
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-gray-700">
          © 2026 Dobler Data Solutions, LLC
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
